import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Check, CheckCheck, ExternalLink, Newspaper, AlertTriangle, Info, UserCheck, ChevronRight, ChevronLeft, Trash2, Mail, MessageSquare, Send, Loader2, Paperclip, Image, FileText, Link2, XCircle, Download, ZoomIn, Plus, Search, Users, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ConfirmModal from './ConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Notification types that are alerts (not conversations)
const ALERT_TYPES = ['article_approved', 'article_rejected', 'article_pending', 'article_submitted', 'contributor_approved', 'contributor_request', 'system', 'welcome', 'push', 'announcement', 'news', 'breaking'];

// Notification types only for admins
const ADMIN_ONLY_TYPES = ['contributor_request', 'admin', 'user_management', 'pending_review'];

// Context Menu Component - Now includes all bulk actions
const ContextMenu = ({ 
  x, y, onClose, onMarkRead, onMarkUnread, onDelete, onSelect, isRead, isSelected,
  onMarkAllRead, onSelectAll, onDeselectAll, onDeleteSelected, onDeleteAll,
  selectedCount, totalCount, hasUnread, itemType
}) => {
  const menuRef = useRef(null);
  const isContact = itemType === 'contact';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose]);

  // Calculate position - center on mobile, normal on desktop
  const isMobile = window.innerWidth < 640;
  const menuWidth = isMobile ? Math.min(280, window.innerWidth - 32) : 170;
  const adjustedX = isMobile ? (window.innerWidth - menuWidth) / 2 : Math.min(x, window.innerWidth - menuWidth - 16);
  const adjustedY = isMobile ? Math.min(y, window.innerHeight - 350) : Math.min(y, window.innerHeight - 280);

  const allSelected = selectedCount > 0 && selectedCount === totalCount;

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && (
        <div className="fixed inset-0 bg-black/30 z-[59]" onClick={onClose} />
      )}
      <div
        ref={menuRef}
        className={`fixed z-[60] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2 ${isMobile ? 'mx-4' : ''}`}
        style={{ 
          left: adjustedX, 
          top: adjustedY,
          width: isMobile ? menuWidth : 'auto',
          minWidth: isMobile ? 'auto' : '170px'
        }}
      >
      {/* Single item actions */}
      <div className={totalCount > 0 ? "border-b border-gray-100 dark:border-gray-700 pb-2 mb-2" : ""}>
        <button
          onClick={onSelect}
          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <CheckCircle className={`h-4 w-4 ${isSelected ? 'text-emerald-500' : 'text-gray-400'}`} />
          <span>{isSelected ? 'Deselect' : 'Select'}</span>
        </button>
        {/* Mark as read - for notifications and contacts with unread messages */}
        {!isRead && (
          <button
            onClick={onMarkRead}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Check className="h-4 w-4 text-emerald-500" />
            <span>Read</span>
          </button>
        )}
        {/* Mark as unread - for read notifications and contacts */}
        {isRead && (
          <button
            onClick={onMarkUnread}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Mail className="h-4 w-4 text-blue-500" />
            <span>Unread</span>
          </button>
        )}
        {/* Delete conversation - for contacts */}
        {isContact && (
          <button
            onClick={onDelete}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete conversation</span>
          </button>
        )}
        {/* Delete - for notifications */}
        {!isContact && (
          <button
            onClick={onDelete}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        )}
      </div>

      {/* Bulk actions */}
      {totalCount > 0 && (
        <div className="pt-1">
          <p className="px-4 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Bulk Actions</p>
          
          {/* Select All - show when not all selected */}
          {!allSelected && (
            <button
              onClick={onSelectAll}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              <span>Select all ({totalCount})</span>
            </button>
          )}
          
          {/* Deselect All - show when any items are selected */}
          {selectedCount > 0 && (
            <button
              onClick={onDeselectAll}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4 text-gray-400" />
              <span>Deselect all ({selectedCount})</span>
            </button>
          )}
          
          {/* Mark all as read */}
          {hasUnread && (
            <button
              onClick={onMarkAllRead}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <CheckCheck className="h-4 w-4 text-blue-500" />
              <span>Mark all as read</span>
            </button>
          )}
          
          {/* Delete selected */}
          {selectedCount > 0 && (
            <button
              onClick={onDeleteSelected}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete selected ({selectedCount})</span>
            </button>
          )}
          
          {/* Delete all */}
          <button
            onClick={onDeleteAll}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete all</span>
          </button>
        </div>
      )}
      </div>
    </>
  );
};

// getAttachmentUrl helper function

// Helper to get full URL for attachments
const getAttachmentUrl = (att) => {
  if (!att.url) return '';
  if (att.url.startsWith('http://') || att.url.startsWith('https://')) return att.url;
  return `${BACKEND_URL}${att.url}`;
};

// Image Lightbox
const ImageLightbox = ({ src, alt, onClose }) => {
  const handleDownload = async () => {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = alt || 'image';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { window.open(src, '_blank'); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[90vh] w-full">
        <button onClick={onClose} className="absolute -top-12 right-0 p-2 text-white/80 hover:text-white"><X className="h-6 w-6" /></button>
        <img src={src} alt={alt} className="max-w-full max-h-[80vh] mx-auto rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
        <div className="absolute -bottom-14 left-1/2 -translate-x-1/2">
          <button onClick={(e) => { e.stopPropagation(); handleDownload(); }} className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full">
            <Download className="h-4 w-4" /><span className="text-sm">Save</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Attachment Preview
const AttachmentPreview = ({ attachment, onRemove }) => (
  <div className="relative inline-flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
    {attachment.type === 'image' ? <Image className="h-4 w-4 text-emerald-500" /> : 
     attachment.type === 'link' ? <Link2 className="h-4 w-4 text-blue-500" /> : 
     <FileText className="h-4 w-4 text-purple-500" />}
    <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[120px]">{attachment.name}</span>
    <button onClick={onRemove} className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full">
      <XCircle className="h-4 w-4 text-gray-400 hover:text-red-500" />
    </button>
  </div>
);

// Chat Bubble
const ChatBubble = ({ message, isOwn, timestamp, senderName, attachments, onImageClick, isRead }) => {
  const formatTime = (dateString) => new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className="max-w-[80%]">
        {!isOwn && senderName && <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 ml-3">{senderName}</p>}
        
        {attachments && attachments.length > 0 && (
          <div className="mb-2 space-y-2">
            {attachments.map((att, index) => {
              const fullUrl = getAttachmentUrl(att);
              return (
                <div key={index} className={`rounded-xl overflow-hidden ${isOwn ? 'ml-auto' : ''}`}>
                  {att.type === 'image' ? (
                    <button onClick={() => onImageClick?.(fullUrl, att.name)} className="block cursor-zoom-in relative group">
                      <img src={fullUrl} alt={att.name || 'Image'} className="max-w-full max-h-48 rounded-xl object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-xl flex items-center justify-center">
                        <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100" />
                      </div>
                    </button>
                  ) : (
                    <a href={fullUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 px-3 py-2 rounded-xl ${isOwn ? 'bg-emerald-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'}`}>
                      <FileText className="h-4 w-4" /><span className="text-sm truncate">{att.name || 'Document'}</span><Download className="h-3 w-3 opacity-70" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {message && (
          <div className={`px-4 py-2.5 rounded-2xl ${isOwn ? 'bg-emerald-500 text-white rounded-br-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-md'}`}>
            <p className="text-sm whitespace-pre-wrap break-words">{message}</p>
          </div>
        )}
        
        <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end mr-1' : 'justify-start ml-3'}`}>
          <span className="text-[10px] text-gray-400">{formatTime(timestamp)}</span>
          {/* Read receipts: single check = sent, double check = read, blue double check = read */}
          {isOwn && (
            isRead ? (
              <CheckCheck className="h-3 w-3 text-blue-500" />
            ) : (
              <Check className="h-3 w-3 text-gray-400" />
            )
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
const NotificationPanel = ({ isOpen, onClose, unreadCount, setUnreadCount, onUnreadCountChange }) => {
  // Tab: 'alerts' | 'messages'
  const [activeTab, setActiveTab] = useState('alerts');
  // View: 'list' | 'conversation' | 'selectContact'
  const [currentView, setCurrentView] = useState('list');
  
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(true);
  
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  // Chat message context menu (for messages within a conversation)
  const [chatContextMenu, setChatContextMenu] = useState(null);
  // Selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  // Selected chat messages (within a conversation)
  const [selectedChatMsgs, setSelectedChatMsgs] = useState(new Set());
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'danger' });
  // Long press state for mobile
  const longPressTimer = useRef(null);
  const longPressMsgTimer = useRef(null);
  
  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatContextMenuRef = useRef(null);
  const messageInputRef = useRef(null);
  
  const { isAuthenticated, isAdmin, isContributor, canWrite, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // Only contributors and admins can use messaging
  const canMessage = canWrite || isAdmin || isContributor;

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchNotifications();
      if (canMessage) {
        fetchMessageContacts();
      }
    }
    if (!isOpen) {
      setCurrentView('list');
      setSelectedContact(null);
      setConversationMessages([]);
      setNewMessage('');
      setAttachments([]);
    }
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    if (currentView === 'conversation') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationMessages, currentView]);

  // Handle click outside for chat context menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (chatContextMenu && chatContextMenuRef.current && !chatContextMenuRef.current.contains(e.target)) {
        setChatContextMenu(null);
      }
    };
    
    if (chatContextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [chatContextMenu]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const response = await axios.get(`${BACKEND_URL}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      
      let filtered = response.data;
      if (!isAdmin) {
        filtered = response.data.filter(n => 
          !ADMIN_ONLY_TYPES.includes(n.type) && 
          !n.title?.toLowerCase().includes('new contributor') &&
          !n.message?.toLowerCase().includes('has applied to become')
        );
      }
      
      // Separate alerts and messages
      const alerts = filtered.filter(n => ALERT_TYPES.includes(n.type));
      const msgs = filtered.filter(n => !ALERT_TYPES.includes(n.type));
      
      // Sort both by newest first
      alerts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      msgs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setNotifications(alerts);
      setMessages(msgs);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
      setLoadingMessages(false);
    }
  };

  const fetchMessageContacts = async () => {
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const response = await axios.get(`${BACKEND_URL}/api/inbox/contacts`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchConversation = async (contactId) => {
    setSelectedChatMsgs(new Set()); // Clear selections when opening new conversation
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const response = await axios.get(`${BACKEND_URL}/api/inbox/conversation-with/${contactId}`, { headers: { Authorization: `Bearer ${token}` } });
      const msgs = response.data.messages || [];
      // Sort ASCENDING for chat (oldest first, new messages at bottom)
      msgs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setConversationMessages(msgs);
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
      setConversationMessages([]);
    }
  };

  const fetchAllContacts = async () => {
    setLoadingContacts(true);
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const response = await axios.get(`${BACKEND_URL}/api/inbox/contacts`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.post(`${BACKEND_URL}/api/notifications/mark-read`, { notification_id: notificationId }, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setMessages(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      if (setUnreadCount) setUnreadCount(prev => Math.max(0, prev - 1));
      if (onUnreadCountChange) onUnreadCountChange();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAsUnread = async (notificationId) => {
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.post(`${BACKEND_URL}/api/notifications/mark-unread`, { notification_id: notificationId }, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: false } : n));
      setMessages(prev => prev.map(n => n.id === notificationId ? { ...n, read: false } : n));
      if (setUnreadCount) setUnreadCount(prev => prev + 1);
      if (onUnreadCountChange) onUnreadCountChange();
    } catch (error) {
      console.error('Failed to mark as unread:', error);
    }
  };

  const handleMarkConversationAsRead = async (contactId) => {
    try {
      const token = localStorage.getItem('yegoNewsToken');
      const response = await axios.post(`${BACKEND_URL}/api/inbox/mark-conversation-read`, { contact_id: contactId }, { headers: { Authorization: `Bearer ${token}` } });
      // Refresh contacts list and notifications to get updated data from server
      await fetchMessageContacts();
      await fetchNotifications();
      // Update unread count by the number of messages marked as read
      const markedCount = response.data.count || 0;
      if (setUnreadCount && markedCount > 0) setUnreadCount(prev => Math.max(0, prev - markedCount));
      if (onUnreadCountChange) onUnreadCountChange();
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  };

  const handleMarkConversationAsUnread = async (contactId) => {
    try {
      const token = localStorage.getItem('yegoNewsToken');
      // Only mark the LAST message as unread, not the whole conversation
      const response = await axios.post(`${BACKEND_URL}/api/inbox/mark-last-message-unread`, { contact_id: contactId }, { headers: { Authorization: `Bearer ${token}` } });
      // Refresh contacts list and notifications to get updated data from server
      await fetchMessageContacts();
      await fetchNotifications();
      // Update unread count by the number of messages marked as unread (should be 1)
      const markedCount = response.data.count || 0;
      if (setUnreadCount && markedCount > 0) setUnreadCount(prev => prev + markedCount);
      if (onUnreadCountChange) onUnreadCountChange();
    } catch (error) {
      console.error('Failed to mark conversation as unread:', error);
    }
  };

  const handleDeleteConversation = async (contactId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Conversation',
      message: 'Are you sure you want to delete this entire conversation? This action cannot be undone.',
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('yegoNewsToken');
          const response = await axios.delete(`${BACKEND_URL}/api/inbox/delete-conversation/${contactId}`, { headers: { Authorization: `Bearer ${token}` } });
          // Refresh contacts list and notifications
          await fetchMessageContacts();
          await fetchNotifications();
          // Update unread count
          const deletedUnread = response.data.unread_deleted || 0;
          if (setUnreadCount && deletedUnread > 0) setUnreadCount(prev => Math.max(0, prev - deletedUnread));
          if (onUnreadCountChange) onUnreadCountChange();
        } catch (error) {
          console.error('Failed to delete conversation:', error);
        }
        setContextMenu(null);
        setConfirmModal({ isOpen: false });
      }
    });
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.post(`${BACKEND_URL}/api/notifications/mark-all-read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setMessages(prev => prev.map(n => ({ ...n, read: true })));
      if (setUnreadCount) setUnreadCount(0);
      if (onUnreadCountChange) onUnreadCountChange();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDeleteSingle = async (notificationId, isRead) => {
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.delete(`${BACKEND_URL}/api/notifications/${notificationId}`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setMessages(prev => prev.filter(n => n.id !== notificationId));
      setSelectedItems(prev => { const newSet = new Set(prev); newSet.delete(notificationId); return newSet; });
      if (!isRead && setUnreadCount) setUnreadCount(prev => Math.max(0, prev - 1));
      if (!isRead && onUnreadCountChange) onUnreadCountChange();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
    setContextMenu(null);
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    
    // Different logic for alerts vs messages (contacts)
    if (activeTab === 'alerts') {
      // Delete selected alerts/notifications
      setConfirmModal({
        isOpen: true,
        title: 'Delete Selected Alerts',
        message: `Are you sure you want to delete ${selectedItems.size} selected alert(s)? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'danger',
        onConfirm: async () => {
          const token = localStorage.getItem('yegoNewsToken');
          let deletedCount = 0;
          let unreadDeleted = 0;
          
          for (const id of selectedItems) {
            try {
              const item = notifications.find(n => n.id === id);
              await axios.delete(`${BACKEND_URL}/api/notifications/${id}`, { headers: { Authorization: `Bearer ${token}` } });
              if (item && !item.read) unreadDeleted++;
              deletedCount++;
            } catch (error) {
              console.error('Failed to delete:', id, error);
            }
          }
          
          setNotifications(prev => prev.filter(n => !selectedItems.has(n.id)));
          setSelectedItems(new Set());
          if (setUnreadCount) setUnreadCount(prev => Math.max(0, prev - unreadDeleted));
          if (unreadDeleted > 0 && onUnreadCountChange) onUnreadCountChange();
          setConfirmModal({ isOpen: false });
        }
      });
    } else {
      // Delete selected conversations (contacts)
      setConfirmModal({
        isOpen: true,
        title: 'Delete Selected Conversations',
        message: `Are you sure you want to delete ${selectedItems.size} conversation(s)? All messages will be permanently deleted.`,
        confirmText: 'Delete',
        type: 'danger',
        onConfirm: async () => {
          const token = localStorage.getItem('yegoNewsToken');
          
          for (const contactId of selectedItems) {
            try {
              await axios.delete(`${BACKEND_URL}/api/inbox/delete-conversation/${contactId}`, 
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } catch (error) {
              console.error('Failed to delete conversation:', contactId, error);
            }
          }
          
          // Remove deleted contacts from list
          setContacts(prev => prev.filter(c => !selectedItems.has(c.id)));
          setSelectedItems(new Set());
          // Refresh to update counts
          fetchMessageContacts();
          if (onUnreadCountChange) onUnreadCountChange();
          setConfirmModal({ isOpen: false });
        }
      });
    }
  };

  // Delete all items (alerts or conversations)
  const handleDeleteAll = async () => {
    if (activeTab === 'alerts') {
      if (notifications.length === 0) return;
      
      setConfirmModal({
        isOpen: true,
        title: 'Delete All Alerts',
        message: `Are you sure you want to delete all ${notifications.length} alert(s)? This action cannot be undone.`,
        confirmText: 'Delete All',
        type: 'danger',
        onConfirm: async () => {
          const token = localStorage.getItem('yegoNewsToken');
          
          for (const notification of notifications) {
            try {
              await axios.delete(`${BACKEND_URL}/api/notifications/${notification.id}`, { headers: { Authorization: `Bearer ${token}` } });
            } catch (error) {
              console.error('Failed to delete:', notification.id, error);
            }
          }
          
          setNotifications([]);
          setSelectedItems(new Set());
          if (setUnreadCount) setUnreadCount(0);
          if (onUnreadCountChange) onUnreadCountChange();
          setConfirmModal({ isOpen: false });
        }
      });
    } else {
      const conversationContacts = contacts.filter(c => c.last_message);
      if (conversationContacts.length === 0) return;
      
      setConfirmModal({
        isOpen: true,
        title: 'Delete All Conversations',
        message: `Are you sure you want to delete all ${conversationContacts.length} conversation(s)? All messages will be permanently deleted.`,
        confirmText: 'Delete All',
        type: 'danger',
        onConfirm: async () => {
          const token = localStorage.getItem('yegoNewsToken');
          
          for (const contact of conversationContacts) {
            try {
              await axios.delete(`${BACKEND_URL}/api/inbox/delete-conversation/${contact.id}`, 
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } catch (error) {
              console.error('Failed to delete conversation:', contact.id, error);
            }
          }
          
          setContacts(prev => prev.filter(c => !c.last_message));
          setSelectedItems(new Set());
          fetchMessageContacts();
          if (onUnreadCountChange) onUnreadCountChange();
          setConfirmModal({ isOpen: false });
        }
      });
    }
  };

  const handleToggleSelect = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
    setContextMenu(null);
  };

  const handleSelectAll = () => {
    // For Messages tab, only select contacts that have conversations (have last_message)
    // For Alerts tab, select all notifications
    if (activeTab === 'alerts') {
      const allIds = new Set(notifications.map(item => item.id));
      setSelectedItems(allIds);
    } else {
      // Only select contacts with existing conversations
      const conversationContacts = contacts.filter(c => c.last_message);
      const allIds = new Set(conversationContacts.map(item => item.id));
      setSelectedItems(allIds);
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
  };

  // Chat message selection handlers
  const handleChatMsgContextMenu = (e, msg) => {
    e.preventDefault();
    e.stopPropagation();
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 250);
    setChatContextMenu({ x, y, item: msg });
  };

  const handleToggleChatMsgSelect = (msgId) => {
    setSelectedChatMsgs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(msgId)) {
        newSet.delete(msgId);
      } else {
        newSet.add(msgId);
      }
      return newSet;
    });
  };

  const handleSelectAllChatMsgs = () => {
    const allIds = new Set(conversationMessages.map(m => m.id));
    setSelectedChatMsgs(allIds);
  };

  const handleDeselectAllChatMsgs = () => {
    setSelectedChatMsgs(new Set());
  };

  const handleDeleteChatMsg = async (msgId) => {
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.delete(`${BACKEND_URL}/api/notifications/${msgId}`, { headers: { Authorization: `Bearer ${token}` } });
      setConversationMessages(prev => prev.filter(m => m.id !== msgId));
      setSelectedChatMsgs(prev => { const newSet = new Set(prev); newSet.delete(msgId); return newSet; });
      // Refresh contacts to update counts
      fetchMessageContacts();
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  };

  const handleDeleteSelectedChatMsgs = async () => {
    if (selectedChatMsgs.size === 0) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Selected Messages',
      message: `Are you sure you want to delete ${selectedChatMsgs.size} message(s)?`,
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('yegoNewsToken');
          const idsToDelete = Array.from(selectedChatMsgs);
          
          // Delete all messages and wait for all to complete
          await Promise.all(idsToDelete.map(msgId => 
            axios.delete(`${BACKEND_URL}/api/notifications/${msgId}`, { headers: { Authorization: `Bearer ${token}` } })
          ));
          
          // Update local state after successful deletion
          setConversationMessages(prev => prev.filter(m => !selectedChatMsgs.has(m.id)));
          setSelectedChatMsgs(new Set());
          // Refresh contacts to update counts
          await fetchMessageContacts();
        } catch (error) {
          console.error('Failed to delete messages:', error);
        }
        setConfirmModal({ isOpen: false });
      }
    });
  };

  const handleContextMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, item });
  };

  // Long press handlers for mobile context menu
  const handleTouchStart = (e, item, type = 'notification') => {
    longPressTimer.current = setTimeout(() => {
      e.preventDefault();
      const touch = e.touches[0];
      if (type === 'contact') {
        setContextMenu({ x: touch.clientX, y: touch.clientY, item, isContact: true });
      } else {
        setContextMenu({ x: touch.clientX, y: touch.clientY, item });
      }
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTouchMove = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Long press for chat messages
  const handleMsgTouchStart = (e, msg) => {
    longPressMsgTimer.current = setTimeout(() => {
      e.preventDefault();
      const touch = e.touches[0];
      setChatContextMenu({ x: touch.clientX, y: touch.clientY, item: msg });
    }, 500);
  };

  const handleMsgTouchEnd = () => {
    if (longPressMsgTimer.current) {
      clearTimeout(longPressMsgTimer.current);
      longPressMsgTimer.current = null;
    }
  };

  const handleMsgTouchMove = () => {
    if (longPressMsgTimer.current) {
      clearTimeout(longPressMsgTimer.current);
      longPressMsgTimer.current = null;
    }
  };

  const handleAlertClick = (notification) => {
    if (!notification.read) handleMarkAsRead(notification.id);
    setSelectedAlert(notification);
    setCurrentView('alertDetail');
  };

  const handleAlertAction = () => {
    // Navigate to link if available, then close
    if (selectedAlert?.link) {
      navigate(selectedAlert.link);
      onClose();
    }
  };

  const handleMessageClick = async (msg) => {
    if (!msg.read) handleMarkAsRead(msg.id);
    
    // Determine the contact ID - it's the OTHER person in the conversation
    // If I sent the message (sender_id === my id), contact is user_id (recipient)
    // If I received the message (user_id === my id), contact is sender_id (sender)
    let contactId = null;
    let contactName = 'User';
    
    if (msg.sender_id === user?.id) {
      // I sent this message, so the contact is the recipient
      contactId = msg.user_id;
      contactName = msg.recipient_name || 'User';
    } else if (msg.sender_id) {
      // Someone else sent this to me
      contactId = msg.sender_id;
      contactName = msg.sender_name || 'User';
    } else if (msg.user_id && msg.user_id !== user?.id) {
      // Fallback: user_id is the other person
      contactId = msg.user_id;
      contactName = msg.sender_name || 'User';
    }
    
    if (!contactId) {
      console.error('Could not determine contact ID from message:', msg);
      return;
    }
    
    setSelectedContact({ id: contactId, name: contactName, email: '' });
    await fetchConversation(contactId);
    setCurrentView('conversation');
  };

  const handleNewMessageClick = () => {
    fetchAllContacts();
    setCurrentView('selectContact');
  };

  const handleSelectContact = async (contact) => {
    setSelectedContact({ id: contact.id, name: contact.name, email: contact.email });
    await fetchConversation(contact.id);
    setCurrentView('conversation');
    // Auto-focus on input when opening chat
    setTimeout(() => messageInputRef.current?.focus(), 100);
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && attachments.length === 0) || sending || !selectedContact) return;
    setSending(true);
    try {
      const token = localStorage.getItem('yegoNewsToken');
      await axios.post(`${BACKEND_URL}/api/inbox/send-direct`, {
        recipient_id: selectedContact.id,
        title: 'Message',
        message: newMessage,
        attachments: attachments
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const newMsg = { id: Date.now().toString(), message: newMessage, sender_id: user?.id, sender_name: user?.name || 'You', created_at: new Date().toISOString(), attachments };
      setConversationMessages(prev => [...prev, newMsg]);
      setNewMessage('');
      setAttachments([]);
      // Keep focus on input for quick follow-up messages
      setTimeout(() => messageInputRef.current?.focus(), 50);
    } catch (error) {
      console.error('Failed to send:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // File uploads
  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${BACKEND_URL}/api/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachments(prev => [...prev, { type: 'image', url: response.data.image_url || response.data.url, name: file.name }]);
    } catch { alert('Failed to upload image'); }
    finally { setUploading(false); setShowAttachMenu(false); }
  };

  const handleDocumentUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${BACKEND_URL}/api/upload-document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachments(prev => [...prev, { type: 'document', url: response.data.url, name: file.name }]);
    } catch { alert('Failed to upload document'); }
    finally { setUploading(false); setShowAttachMenu(false); }
  };

  const handleImageSelect = (e) => { const file = e.target.files?.[0]; if (file) { if (file.size > 10*1024*1024) { alert('Max 10MB'); return; } handleImageUpload(file); } e.target.value = ''; };
  const handleDocumentSelect = (e) => { const file = e.target.files?.[0]; if (file) { if (file.size > 25*1024*1024) { alert('Max 25MB'); return; } handleDocumentUpload(file); } e.target.value = ''; };
  const handleAddLink = () => { const url = prompt('Enter URL:'); if (url?.trim()) setNewMessage(prev => prev + (prev ? ' ' : '') + url.trim()); setShowAttachMenu(false); };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  };

  const getIcon = (type) => {
    switch(type) {
      case 'article_approved': return <Check className="h-5 w-5 text-emerald-500" />;
      case 'article_rejected': return <X className="h-5 w-5 text-red-500" />;
      case 'article_pending': case 'article_submitted': return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'contributor_approved': return <UserCheck className="h-5 w-5 text-emerald-500" />;
      default: return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const filteredContacts = contacts.filter(c => c.name?.toLowerCase().includes(contactSearch.toLowerCase()) || c.email?.toLowerCase().includes(contactSearch.toLowerCase()));
  const unreadAlerts = notifications.filter(n => !n.read).length;
  const unreadMessages = messages.filter(m => !m.read).length;

  if (!isOpen) return null;

  return (
    <>
      <input type="file" ref={imageInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />
      <input type="file" ref={fileInputRef} onChange={handleDocumentSelect} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" className="hidden" />

      {/* Backdrop - click outside to close */}
      <div 
        className="fixed inset-0 z-40 bg-black/20 sm:bg-transparent" 
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="fixed inset-x-0 top-0 bottom-12 sm:inset-auto sm:right-0 sm:top-0 sm:bottom-12 sm:w-96 bg-white dark:bg-gray-900 shadow-2xl z-50 flex flex-col">
        
        {/* === LIST VIEW === */}
        {currentView === 'list' && (
          <div className="flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {activeTab === 'alerts' ? 'Notifications' : 'Messages'}
                  {selectedItems.size > 0 && (
                    <span className="ml-2 text-sm font-normal text-gray-500">({selectedItems.size} selected)</span>
                  )}
                </h2>
                <div className="flex items-center gap-1">
                  {/* New Message button - only on Messages tab for contributors/admins */}
                  {activeTab === 'messages' && canMessage && (
                    <button 
                      onClick={handleNewMessageClick} 
                      className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-full text-emerald-600"
                      title="New Message"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  )}
                  <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>
              
              {/* Tab Icons */}
              <div className="flex border-t border-gray-200 dark:border-gray-800">
                <button
                  onClick={() => { setActiveTab('alerts'); setSelectedItems(new Set()); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'alerts' 
                      ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <Bell className="h-5 w-5" />
                  <span>Alerts</span>
                  {unreadAlerts > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">{unreadAlerts}</span>
                  )}
                </button>
                {/* Messages tab only for contributors and admins */}
                {canMessage && (
                  <button
                    onClick={() => { setActiveTab('messages'); setSelectedItems(new Set()); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                      activeTab === 'messages' 
                        ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <MessageSquare className="h-5 w-5" />
                    <span>Messages</span>
                    {unreadMessages > 0 && (
                      <span className="px-1.5 py-0.5 text-xs bg-emerald-500 text-white rounded-full">{unreadMessages}</span>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
              {/* ALERTS TAB */}
              {activeTab === 'alerts' && (
                loading ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
                ) : notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Bell className="h-12 w-12 mb-3 opacity-50" /><p>No notifications</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {notifications.map(notification => {
                      const isSelected = selectedItems.has(notification.id);
                      return (
                        <div
                          key={notification.id}
                          className={`p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${!notification.read ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : ''} ${isSelected ? 'bg-emerald-100/50 dark:bg-emerald-900/30' : ''}`}
                          onClick={() => handleAlertClick(notification)}
                          onContextMenu={(e) => handleContextMenu(e, notification)}
                          onTouchStart={(e) => handleTouchStart(e, notification)}
                          onTouchEnd={handleTouchEnd}
                          onTouchMove={handleTouchMove}
                        >
                          <div className="flex gap-2 sm:gap-3">
                            {/* Selection checkbox */}
                            {selectedItems.size > 0 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleToggleSelect(notification.id); }}
                                className="flex-shrink-0 self-center"
                              >
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 dark:border-gray-600'}`}>
                                  {isSelected && <Check className="h-3 w-3 text-white" />}
                                </div>
                              </button>
                            )}
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                              {getIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <h4 className={`text-xs sm:text-sm font-medium truncate ${!notification.read ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                  YEGO News
                                </h4>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-400">{formatTime(notification.created_at)}</span>
                                  {!notification.read && <span className="w-2 h-2 bg-red-500 rounded-full" />}
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 truncate">{notification.title}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{notification.message}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* MESSAGES TAB */}
              {activeTab === 'messages' && (
                loadingMessages || loadingContacts ? (
                  <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
                ) : contacts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <MessageSquare className="h-12 w-12 mb-3 opacity-50" /><p>No contacts available</p>
                    <button onClick={handleNewMessageClick} className="mt-4 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-sm flex items-center gap-2">
                      <Plus className="h-4 w-4" /> New Message
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {/* Show contacts with conversations (like WhatsApp) */}
                    {contacts.filter(c => c.last_message).map(contact => {
                      const isFromAdmin = contact.role === 'admin' || contact.is_admin;
                      const hasUnread = contact.unread_count > 0;
                      const isSelected = selectedItems.has(contact.id);
                      return (
                        <div
                          key={contact.id}
                          className={`p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${hasUnread ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''} ${isSelected ? 'bg-emerald-100/50 dark:bg-emerald-900/30' : ''}`}
                          onClick={() => handleSelectContact(contact)}
                          onContextMenu={(e) => handleContextMenu(e, { ...contact, id: contact.id, read: !hasUnread, type: 'contact' })}
                          onTouchStart={(e) => handleTouchStart(e, { ...contact, id: contact.id, read: !hasUnread, type: 'contact' }, 'contact')}
                          onTouchEnd={handleTouchEnd}
                          onTouchMove={handleTouchMove}
                        >
                          <div className="flex gap-2 sm:gap-3">
                            {/* Selection checkbox */}
                            {selectedItems.size > 0 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleToggleSelect(contact.id); }}
                                className="flex-shrink-0 self-center"
                              >
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 dark:border-gray-600'}`}>
                                  {isSelected && <Check className="h-3 w-3 text-white" />}
                                </div>
                              </button>
                            )}
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-medium flex-shrink-0 ${isFromAdmin ? 'bg-gradient-to-br from-purple-400 to-purple-600' : 'bg-gradient-to-br from-blue-400 to-blue-600'}`}>
                              {contact.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <h4 className={`text-xs sm:text-sm font-medium truncate ${hasUnread ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
                                  {contact.name || 'User'}
                                </h4>
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] text-gray-400">{contact.last_message?.created_at ? formatTime(contact.last_message.created_at) : ''}</span>
                                  {hasUnread && <span className="px-1.5 py-0.5 text-[10px] bg-emerald-500 text-white rounded-full">{contact.unread_count}</span>}
                                </div>
                              </div>
                              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5 sm:mt-1 line-clamp-1">
                                {contact.last_message?.sender_id === user?.id ? 'You: ' : ''}{contact.last_message?.message || 'No messages yet'}
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 self-center" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* === SELECT CONTACT VIEW === */}
        {currentView === 'selectContact' && (
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <button onClick={() => setCurrentView('list')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
                <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New Message</h2>
            </div>
            
            <div className="p-3 border-b border-gray-200 dark:border-gray-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="text" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border-0 rounded-full text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loadingContacts ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
              ) : filteredContacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mb-3 opacity-50" /><p>No contacts found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredContacts.map(contact => (
                    <div key={contact.id} onClick={() => handleSelectContact(contact)} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-semibold">
                        {contact.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{contact.name}</p>
                        <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* === CONVERSATION VIEW === */}
        {currentView === 'conversation' && selectedContact && (
          <div className="flex flex-col h-full relative">
            {/* Fixed Header */}
            <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-emerald-500 to-emerald-600 flex-shrink-0 sticky top-0 z-10">
              <button onClick={() => { setCurrentView('list'); setSelectedContact(null); setConversationMessages([]); setSelectedChatMsgs(new Set()); fetchMessageContacts(); fetchNotifications(); if (onUnreadCountChange) onUnreadCountChange(); }} className="p-1.5 sm:p-2 hover:bg-white/20 rounded-full">
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center text-white text-sm sm:text-base font-semibold">
                {selectedContact.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white text-xs sm:text-sm truncate">{selectedContact.name}</h3>
                <p className="text-[10px] sm:text-xs text-white/80 truncate">{selectedContact.email || 'YEGO News'}</p>
              </div>
            </div>

            {/* Messages Area - scrollable */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 bg-gray-50 dark:bg-gray-950 pb-20">
              {conversationMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <MessageSquare className="h-10 w-10 sm:h-12 sm:w-12 mb-2 opacity-50" /><p className="text-xs sm:text-sm">Start a conversation</p>
                </div>
              ) : (
                <>
                  {conversationMessages.map((msg, index) => {
                    const isOwn = msg.sender_id === user?.id;
                    const isMsgSelected = selectedChatMsgs.has(msg.id);
                    return (
                      <div 
                        key={msg.id || index}
                        className={`flex items-start gap-1.5 sm:gap-2 ${isOwn ? 'justify-end' : 'justify-start'} ${isMsgSelected ? 'bg-blue-50 dark:bg-blue-900/20 -mx-2 px-2 py-1 rounded-lg' : ''}`}
                        onContextMenu={(e) => handleChatMsgContextMenu(e, msg)}
                        onTouchStart={(e) => handleMsgTouchStart(e, msg)}
                        onTouchEnd={handleMsgTouchEnd}
                        onTouchMove={handleMsgTouchMove}
                      >
                        {/* Selection checkbox - left side for received messages */}
                        {selectedChatMsgs.size > 0 && !isOwn && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleChatMsgSelect(msg.id); }}
                            className="flex-shrink-0 mt-2"
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isMsgSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 dark:border-gray-600'}`}>
                              {isMsgSelected && <Check className="h-3 w-3 text-white" />}
                            </div>
                          </button>
                        )}
                        <ChatBubble 
                          message={msg.message} 
                          isOwn={isOwn} 
                          timestamp={msg.created_at}
                          senderName={!isOwn ? msg.sender_name : null} 
                          attachments={msg.attachments}
                          onImageClick={(src, alt) => setLightboxImage({ src, alt })}
                          isRead={msg.read}
                        />
                        {/* Selection checkbox - right side for sent messages */}
                        {selectedChatMsgs.size > 0 && isOwn && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleChatMsgSelect(msg.id); }}
                            className="flex-shrink-0 mt-2"
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isMsgSelected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 dark:border-gray-600'}`}>
                              {isMsgSelected && <Check className="h-3 w-3 text-white" />}
                            </div>
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {attachments.length > 0 && (
              <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex flex-wrap gap-2">
                {attachments.map((att, index) => (
                  <AttachmentPreview key={index} attachment={att} onRemove={() => setAttachments(prev => prev.filter((_, i) => i !== index))} />
                ))}
              </div>
            )}

            {/* Input Area - sticky bottom */}
            <div className="sticky bottom-0 left-0 right-0 p-2 sm:p-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 z-10">
              {uploading && (
                <div className="flex items-center justify-between mb-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 text-emerald-500 animate-spin" /><span className="text-xs sm:text-sm text-emerald-600">Uploading...</span></div>
                  <button onClick={() => setUploading(false)} className="text-xs text-red-500">Cancel</button>
                </div>
              )}
              
              <div className="flex items-end gap-1.5 sm:gap-2">
                <div className="relative">
                  <button onClick={() => setShowAttachMenu(!showAttachMenu)} disabled={uploading} className="p-2 sm:p-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 hover:text-emerald-500 disabled:opacity-50">
                    <Paperclip className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                  {showAttachMenu && !uploading && (
                    <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 min-w-[140px] sm:min-w-[160px] z-10">
                      <button onClick={() => imageInputRef.current?.click()} className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <Image className="h-4 w-4 text-emerald-500" /><span>Photo</span>
                      </button>
                      <button onClick={() => fileInputRef.current?.click()} className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <FileText className="h-4 w-4 text-purple-500" /><span>Document</span>
                      </button>
                      <button onClick={handleAddLink} className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <Link2 className="h-4 w-4 text-blue-500" /><span>Link</span>
                      </button>
                    </div>
                  )}
                </div>

                <textarea ref={messageInputRef} value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                  placeholder="Type a message..." rows={1} disabled={sending}
                  className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 dark:bg-gray-800 border-0 rounded-full resize-none focus:ring-2 focus:ring-emerald-500 max-h-24 input-no-zoom"
                  style={{ minHeight: '40px', fontSize: '16px', lineHeight: '1.5' }} />
                
                <button onClick={handleSendMessage} disabled={(!newMessage.trim() && attachments.length === 0) || sending}
                  className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-full flex-shrink-0">
                  {sending ? <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" /> : <Send className="h-4 w-4 sm:h-5 sm:w-5" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* === ALERT DETAIL VIEW === */}
        {currentView === 'alertDetail' && selectedAlert && (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-800 ${
              selectedAlert.type === 'article_approved' ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
              selectedAlert.type === 'article_rejected' ? 'bg-gradient-to-r from-red-500 to-red-600' :
              selectedAlert.type === 'contributor_request' ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
              'bg-gradient-to-r from-blue-500 to-blue-600'
            }`}>
              <button onClick={() => { setCurrentView('list'); setSelectedAlert(null); }} className="p-2 hover:bg-white/20 rounded-full">
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                {getIcon(selectedAlert.type)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white text-sm truncate">
                  {selectedAlert.type === 'article_approved' ? 'Article Approved' :
                   selectedAlert.type === 'article_rejected' ? 'Article Rejected' :
                   selectedAlert.type === 'contributor_request' ? 'Contributor Request' :
                   selectedAlert.type === 'push' ? 'Announcement' :
                   'Notification'}
                </h3>
                <p className="text-xs text-white/80">{formatTime(selectedAlert.created_at)}</p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-950">
              <div className="bg-white dark:bg-gray-900 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-800">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  {selectedAlert.title || 'Notification'}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                  {selectedAlert.message}
                </p>
                
                {/* Article info if available */}
                {selectedAlert.article_title && (
                  <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Related Article</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedAlert.article_title}</p>
                  </div>
                )}
                
                {/* Timestamp */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500">
                    Received: {new Date(selectedAlert.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer with action button */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              {selectedAlert.link ? (
                <button 
                  onClick={handleAlertAction}
                  className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  {selectedAlert.type === 'article_approved' || selectedAlert.type === 'article_rejected' ? 'View Article' :
                   selectedAlert.type === 'contributor_request' ? 'Review Request' :
                   'View Details'}
                </button>
              ) : (
                <button 
                  onClick={() => { setCurrentView('list'); setSelectedAlert(null); }}
                  className="w-full py-3 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back to Notifications
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onMarkRead={() => { 
            if (contextMenu.item.type === 'contact') {
              handleMarkConversationAsRead(contextMenu.item.id);
            } else {
              handleMarkAsRead(contextMenu.item.id);
            }
            setContextMenu(null); 
          }}
          onMarkUnread={() => { 
            if (contextMenu.item.type === 'contact') {
              handleMarkConversationAsUnread(contextMenu.item.id);
            } else {
              handleMarkAsUnread(contextMenu.item.id);
            }
            setContextMenu(null); 
          }}
          onDelete={() => {
            if (contextMenu.item.type === 'contact') {
              handleDeleteConversation(contextMenu.item.id);
            } else {
              handleDeleteSingle(contextMenu.item.id, contextMenu.item.read);
            }
          }}
          onSelect={() => handleToggleSelect(contextMenu.item.id)}
          isRead={contextMenu.item.read}
          isSelected={selectedItems.has(contextMenu.item.id)}
          onMarkAllRead={() => { handleMarkAllAsRead(); setContextMenu(null); }}
          onSelectAll={() => { handleSelectAll(); setContextMenu(null); }}
          onDeselectAll={() => { handleDeselectAll(); setContextMenu(null); }}
          onDeleteSelected={() => { handleDeleteSelected(); setContextMenu(null); }}
          onDeleteAll={() => { handleDeleteAll(); setContextMenu(null); }}
          selectedCount={selectedItems.size}
          totalCount={activeTab === 'alerts' ? notifications.length : contacts.filter(c => c.last_message).length}
          hasUnread={(activeTab === 'alerts' ? notifications : contacts.filter(c => c.last_message)).some(n => activeTab === 'alerts' ? !n.read : n.unread_count > 0)}
          itemType={contextMenu.item.type}
        />
      )}

      {/* Chat Message Context Menu */}
      {chatContextMenu && chatContextMenu.item && (
        <>
          {/* Backdrop for mobile */}
          {window.innerWidth < 640 && (
            <div className="fixed inset-0 bg-black/30 z-[59]" onClick={() => setChatContextMenu(null)} />
          )}
          <div
            ref={chatContextMenuRef}
            className="fixed z-[60] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-2"
            style={{ 
              left: window.innerWidth < 640 ? (window.innerWidth - Math.min(260, window.innerWidth - 32)) / 2 : Math.min(chatContextMenu.x, window.innerWidth - 200),
              top: window.innerWidth < 640 ? Math.min(chatContextMenu.y, window.innerHeight - 300) : chatContextMenu.y,
              width: window.innerWidth < 640 ? Math.min(260, window.innerWidth - 32) : 'auto',
              minWidth: window.innerWidth < 640 ? 'auto' : '180px'
            }}
          >
            {/* Single message actions */}
            <div className="border-b border-gray-100 dark:border-gray-700 pb-2 mb-2">
              <button
                onClick={() => { handleToggleChatMsgSelect(chatContextMenu.item.id); setChatContextMenu(null); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {selectedChatMsgs.has(chatContextMenu.item.id) ? <X className="h-4 w-4 text-gray-400" /> : <CheckCircle className="h-4 w-4 text-emerald-500" />}
                <span>{selectedChatMsgs.has(chatContextMenu.item.id) ? 'Deselect' : 'Select'}</span>
              </button>
              
              <button
                onClick={() => { handleDeleteChatMsg(chatContextMenu.item.id); setChatContextMenu(null); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete message</span>
              </button>
            </div>

            {/* Bulk actions */}
            <div className="pt-1">
              <p className="px-4 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Bulk Actions</p>
              
              {selectedChatMsgs.size < conversationMessages.length && (
                <button
                  onClick={() => { handleSelectAllChatMsgs(); setChatContextMenu(null); }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span>Select all ({conversationMessages.length})</span>
                </button>
              )}
              
              {selectedChatMsgs.size > 0 && (
                <>
                  <button
                    onClick={() => { handleDeselectAllChatMsgs(); setChatContextMenu(null); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                    <span>Deselect all ({selectedChatMsgs.size})</span>
                  </button>
                  
                  <button
                    onClick={() => { handleDeleteSelectedChatMsgs(); setChatContextMenu(null); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete selected ({selectedChatMsgs.size})</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {lightboxImage && <ImageLightbox src={lightboxImage.src} alt={lightboxImage.alt} onClose={() => setLightboxImage(null)} />}
      
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        type={confirmModal.type}
      />
    </>
  );
};

export default NotificationPanel;
